import {
  FC,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { ListboxContext, TListbox } from './ListboxContext';
import { Box } from '@apia/theme';
import { getVariant } from '@apia/theme';
import { WaiTypeAhead } from '../waiAriaHelpers';

const ListboxKeyhandler: FC<
  {
    children: ReactNode;
    className: string;
  } & TListbox['ulProps']
> = ({ children, className }) => {
  const {
    listboxActions,
    id,
    getState,
    onConfirmSelection,
    onPressEnterOnNode,
    onPressKeyOnNode,
    variant,
  } = useContext(ListboxContext);

  const typer = useMemo(
    () =>
      getState().showSearchbox
        ? null
        : new WaiTypeAhead({
            onMultipleKeys(characters) {
              listboxActions.searchElementsByStartingCharacters({
                characters,
              });
            },
            onSingleKey(characters) {
              listboxActions.searchElementsByStartingCharacters({
                characters,
              });
            },
          }),
    [getState, listboxActions],
  );

  const shoutConfirmSelection = useCallback(
    (ev: KeyboardEvent) => {
      onConfirmSelection?.({
        originalEvent: ev,
        selectedElements: !getState().allowSelection
          ? []
          : getState().selectedRows.map(
              (current) =>
                document.querySelector<HTMLElement>(
                  `#${id}__row__${current}`,
                ) as HTMLElement,
            ),
        focusedElement: document.querySelector<HTMLElement>(
          `#${id}__row__${getState().focusedRow}`,
        ) as HTMLElement,
      });
    },
    [getState, id, onConfirmSelection],
  );

  return (
    <Box
      {...getVariant(variant)}
      className={className}
      id={id}
      onKeyDown={useCallback(
        (ev: KeyboardEvent) => {
          const cell = (ev.target as HTMLElement)
            .closest<HTMLElement>('.listbox__list')
            ?.querySelector<HTMLElement>('.listbox__row.focused');
          if (!cell)
            throw new Error(
              'There is no cell on keydown event, it must be an error.',
            );

          const state = getState();
          onPressKeyOnNode?.({ ...ev, target: cell, currentTarget: cell });

          if (ev.code === 'Enter') {
            onPressEnterOnNode?.({ ...ev, target: cell, currentTarget: cell });
          }

          if (ev.isDefaultPrevented() || ev.isPropagationStopped()) return;

          if (
            [
              'ArrowRight',
              'ArrowLeft',
              'ArrowDown',
              'ArrowUp',
              'PageUp',
              'PageDown',
            ].includes(ev.code) ||
            (['Home', 'End'].includes(ev.code) && ev.ctrlKey) ||
            // La selección múltiple cuando no se muestra el searchbox se hace
            // con espacio
            (['Space'].includes(ev.code) &&
              state.isMultipleSelection &&
              !state.showSearchbox) ||
            // La selección múltiple siempre puede hacerse con Enter
            (['Enter'].includes(ev.code) && state.isMultipleSelection) ||
            (['KeyA'].includes(ev.code) &&
              state.isMultipleSelection &&
              ev.ctrlKey)
          ) {
            ev.preventDefault();

            switch (ev.code) {
              case 'KeyA':
                listboxActions.selectAll();
                break;
              case 'ArrowRight':
              case 'ArrowDown':
                listboxActions.moveFocusAndToggleSelection({
                  which: 'next',
                  isShiftKey: ev.shiftKey,
                });
                break;
              case 'ArrowLeft':
              case 'ArrowUp':
                listboxActions.moveFocusAndToggleSelection({
                  which: 'previous',
                  isShiftKey: ev.shiftKey,
                });
                break;
              case 'Home':
                listboxActions.moveFocusAndToggleSelection({
                  which: 'first',
                  isShiftKey: ev.shiftKey,
                });
                break;
              case 'End':
                listboxActions.moveFocusAndToggleSelection({
                  which: 'last',
                  isShiftKey: ev.shiftKey,
                });
                break;
              case 'Enter':
                if (ev.ctrlKey) {
                  shoutConfirmSelection(ev);
                }
                break;
              case 'Space':
                listboxActions.updateFocusBySpaceEvent({
                  isShiftKey: ev.shiftKey,
                });
                break;
              case 'PageUp':
                listboxActions.moveFocusAndToggleSelection({
                  which: 'pageUp',
                  isShiftKey: ev.shiftKey,
                });
                break;
              case 'PageDown':
                listboxActions.moveFocusAndToggleSelection({
                  which: 'pageDown',
                  isShiftKey: ev.shiftKey,
                });
                break;
            }
          } else if (
            ev.code === 'Enter' &&
            (!state.allowSelection || !state.isMultipleSelection)
          ) {
            shoutConfirmSelection(ev);
          } else if (typer) {
            typer.type(ev);
          }
        },
        [
          getState,
          listboxActions,
          onPressEnterOnNode,
          onPressKeyOnNode,
          shoutConfirmSelection,
          typer,
        ],
      )}
    >
      {children}
    </Box>
  );
};

export { ListboxKeyhandler };
